import { motion } from "framer-motion";
import {
  ShieldCheck,
  ChevronRight,
  Bell,
  Lock,
  FileText,
  HelpCircle,
  LogOut,
  Landmark,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import Avatar from "../components/Avatar";
import ThemeToggle from "../components/ThemeToggle";

export const ProfileScreen = () => {
  const nav = useNavigate();
  const { partner, transactions, initialBalance } = useApp();
  const totalSent = transactions
    .filter((t) => t.status === "COMPLETED")
    .reduce((s, t) => s + t.amount, 0);
  
  const balance = initialBalance - totalSent;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-full flex flex-col pb-32"
      data-testid="profile-screen"
    >
      <div className="px-6 pt-5 pb-3">
        <h1 className="font-display text-[28px] font-bold tracking-tight dark:text-white">Profile</h1>
      </div>

      <div className="px-6 space-y-4">
        {/* Balance Card */}
        <div className="rounded-3xl bg-emerald-600 text-white p-6 shadow-lg shadow-emerald-900/20">
          <div className="text-[11px] uppercase tracking-widest text-emerald-100 font-bold">
            Available Balance
          </div>
          <div className="font-display text-[32px] font-bold mt-1">
            {balance.toLocaleString(undefined, { minimumFractionDigits: 2 })} kr
          </div>
          <div className="mt-4 flex items-center gap-2 text-[11px] text-emerald-100/80">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
            Funds held by {partner.name}
          </div>
        </div>

        <div className="rounded-3xl bg-gray-900 text-white p-5">
          <div className="flex items-center gap-4">
            <Avatar initials="AV" color="#FDE68A" size={56} />
            <div>
              <div className="text-[15px] font-semibold text-white">Alex Viktor</div>
              <div className="text-[12px] text-gray-400">alex.viktor@shwiip.app</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                Sent
              </div>
              <div className="font-display text-xl font-bold mt-0.5">
                {totalSent.toFixed(2)} kr
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                Transactions
              </div>
              <div className="font-display text-xl font-bold mt-0.5">
                {transactions.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partner disclosure */}
      <div className="px-6 mt-5">
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 flex items-center justify-center flex-shrink-0">
              <Landmark className="w-4 h-4 text-slate-700 dark:text-gray-300" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-gray-400 font-bold">
                Payment Partner
              </div>
              <div className="text-[14px] font-bold text-slate-900 dark:text-white mt-0.5">
                {partner.name}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-gray-400 mt-0.5">{partner.license}</div>
              <div className="inline-flex items-center gap-1.5 mt-2 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 rounded-full px-2 py-0.5">
                <ShieldCheck className="w-3 h-3" strokeWidth={2.5} />
                Regulated PSP
              </div>
            </div>
          </div>
          <div className="text-[11px] text-slate-500 dark:text-gray-400 mt-3 leading-snug">
            Shwiip is <b>not</b> a bank, wallet, or custodian of funds. Shwiip provides a
            technical interface only; all payments are executed by {partner.name}.
          </div>
        </div>
      </div>

      {/* Settings list */}
      <div className="px-6 mt-5">
        <Section label="Account" />
        <ThemeToggle />
        <Item
          icon={Bell}
          label="Notifications"
          testId="profile-notifications"
          onClick={() => nav("/notifications")}
        />
        <Item
          icon={Lock}
          label="Security"
          testId="profile-security"
          onClick={() => nav("/security")}
        />
        <Item
          icon={FileText}
          label="Legal & compliance"
          testId="profile-legal"
          onClick={() => nav("/legal")}
        />
        <Section label="Support" className="mt-3" />
        <Item
          icon={HelpCircle}
          label="Help center"
          testId="profile-help"
          onClick={() => nav("/help")}
        />
        <Item icon={LogOut} label="Log out" testId="profile-logout" danger />
      </div>

      <div className="text-center text-[10px] text-gray-400 mt-6 px-6">
        Shwiip v0.1 · Technical interface only
      </div>
    </motion.div>
  );
};

const Section = ({ label, className = "" }) => (
  <div
    className={
      "text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-2 " + className
    }
  >
    {label}
  </div>
);

const Item = ({ icon: Icon, label, testId, danger, onClick }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={
      "w-full flex items-center gap-3 py-3 px-2 -mx-2 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors " +
      (danger ? "text-red-600" : "text-gray-900 dark:text-white")
    }
  >
    <div
      className={
        "w-9 h-9 rounded-full flex items-center justify-center " +
        (danger ? "bg-red-50 dark:bg-red-900/20" : "bg-gray-100 dark:bg-white/10")
      }
    >
      <Icon className="w-4 h-4" strokeWidth={2.2} />
    </div>
    <span className="flex-1 text-left text-[14px] font-semibold">{label}</span>
    <ChevronRight className="w-4 h-4 text-gray-400" strokeWidth={2.2} />
  </button>
);

export default ProfileScreen;
